import { ArrowRight, Award, Building2, CheckCircle2, Shield, Users, Zap } from 'lucide-react';
import { Container } from '../../components/layout/Container';
import { Badge } from '../../components/ui/badge';
import { CountUp } from '../../components/motion/CountUp';
import { Reveal } from '../../components/motion/Reveal';
import { BreadcrumbListLd } from '../../components/seo/jsonld/BreadcrumbList';
import { ROUTES } from '@shared/constants/routes';

const STATS = [
  { value: 487, suffix: '', label: 'Dossiers traités en 2025', icon: Zap },
  { value: 14, suffix: 'j', label: 'Délai moyen de montage', icon: Shield },
  { value: 98, suffix: '%', label: 'Dossiers acceptés', icon: Award },
  { value: 0, suffix: ' €', label: "d'avance demandée", icon: Building2 },
];

const POLES = [
  {
    icon: Building2,
    title: 'Travaux et rénovation énergétique',
    desc: "Pompes à chaleur, isolation, ventilation, chauffe-eau performant. Réseau d'artisans certifiés partenaires. On cumule toutes les aides pour réduire votre facture.",
    badge: 'Certifié',
  },
  {
    icon: Zap,
    title: 'Courtage énergie pour professionnels',
    desc: "Négociation de vos contrats gaz et électricité, audit de vos factures. Courtier indépendant des fournisseurs d'énergie.",
    badge: 'Courtage',
  },
  {
    icon: Award,
    title: 'Primes énergie pour artisans',
    desc: 'On monte vos dossiers de primes, on les dépose, on les suit. Si le dossier est rejeté, vous ne payez rien.',
    badge: 'Pôle phare',
  },
];

const TIMELINE = [
  {
    year: '2021',
    event: 'Création de Solution Éco France — premier dossier de prime énergie déposé.',
  },
  { year: '2022', event: 'Lancement du courtage énergie pour les professionnels.' },
  { year: '2023', event: 'Certification, agrément mandataire primes énergie, 200 dossiers.' },
  {
    year: '2024',
    event: 'Expansion vers les collectivités, copropriétés et exploitations agricoles.',
  },
  { year: '2025', event: '487 dossiers traités, lancement de notre plateforme digitale.' },
];

const TEAM = [
  {
    name: 'Équipe Direction',
    role: 'Stratégie & développement',
    initials: 'SD',
  },
  {
    name: 'Pôle Technique',
    role: 'Ingénierie primes énergie et travaux',
    initials: 'PT',
  },
  {
    name: 'Pôle Commercial',
    role: 'Partenariats et développement',
    initials: 'PC',
  },
];

export default function AProposPage() {
  return (
    <div className="bg-white">
      {/* ── Hero ── */}
      <div className="border-b border-stone-200 bg-stone-50/60 py-16 md:py-20">
        <Container className="max-w-4xl">
          <Reveal>
            <div className="flex items-center gap-2">
              <Building2 className="h-4 w-4 text-brand-600" strokeWidth={1.5} />
              <p className="eyebrow">Notre histoire</p>
            </div>
            <h1 className="mt-3 text-balance font-display text-display-2 font-bold tracking-tight text-ink">
              Solution Éco France —<br className="hidden sm:block" /> On vous aide à payer moins
              d'énergie
            </h1>
            <p className="mt-5 max-w-2xl text-[16px] leading-relaxed text-stone-600">
              Un seul interlocuteur pour économiser <em>en consommant moins</em> (travaux),{' '}
              <em>en payant moins cher</em> (courtage), et <em>en valorisant vos chantiers</em>{' '}
              (primes énergie).
            </p>
          </Reveal>

          {/* ── Certifications ── */}
          <Reveal delay={0.1} className="mt-8 flex flex-wrap gap-2">
            {[
              'Qualibat',
              'QualiPAC',
              'Qualibois',
              'RGE',
              'Données protégées',
              'Hébergement France',
            ].map((cert) => (
              <Badge key={cert} variant="secondary" className="gap-1.5">
                <CheckCircle2 className="h-3 w-3 text-brand-600" /> {cert}
              </Badge>
            ))}
          </Reveal>
        </Container>
      </div>

      {/* ── Stats ── */}
      <div className="border-b border-stone-200 bg-white py-10">
        <Container className="max-w-4xl">
          <div className="grid gap-5 sm:grid-cols-2 md:grid-cols-4">
            {STATS.map(({ value, suffix, label, icon: Icon }) => (
              <Reveal key={label}>
                <div className="rounded-xl border border-stone-200 bg-stone-50/60 p-5">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand-50">
                    <Icon className="h-4 w-4 text-brand-600" strokeWidth={1.5} />
                  </div>
                  <p className="mt-3 font-display text-2xl font-bold text-ink">
                    <CountUp to={value} duration={1.4} suffix={suffix} />
                  </p>
                  <p className="mt-0.5 text-[12px] text-stone-500">{label}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </div>

      <Container className="max-w-4xl space-y-16 py-14">
        {/* ── 3 Pôles ── */}
        <section>
          <Reveal>
            <h2 className="font-display text-2xl font-bold text-ink">Nos 3 pôles d'expertise</h2>
            <p className="mt-2 text-[14px] text-stone-500">
              Trois métiers complémentaires. Sur le courtage, indépendant des fournisseurs
              d'énergie. Sur les travaux et les primes, transparence sur nos partenariats.
            </p>
          </Reveal>
          <div className="mt-8 space-y-5">
            {POLES.map((pole, i) => (
              <Reveal key={pole.title} delay={i * 0.06}>
                <div className="flex gap-5 rounded-xl border border-stone-200 bg-stone-50/40 p-6">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-brand-50">
                    <pole.icon className="h-5 w-5 text-brand-600" strokeWidth={1.5} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-display text-[17px] font-semibold text-ink">
                        {pole.title}
                      </h3>
                      <Badge variant="secondary" className="text-[10px]">
                        {pole.badge}
                      </Badge>
                    </div>
                    <p className="mt-1.5 text-[14px] leading-relaxed text-stone-600">{pole.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </section>

        {/* ── Modèle économique ── */}
        <section>
          <Reveal>
            <h2 className="font-display text-2xl font-bold text-ink">
              Modèle économique transparent
            </h2>
          </Reveal>
          <Reveal delay={0.05}>
            <div className="mt-5 rounded-xl border border-stone-200 bg-stone-50/40 p-6">
              <p className="text-[15px] leading-relaxed text-stone-700">
                Trois métiers, trois modes de rémunération : prestation sur les travaux, commission
                fournisseur sur le courtage, mandat sur les primes énergie. Tout est détaillé dans
                notre{' '}
                <a
                  className="font-semibold text-brand-700 underline-offset-2 hover:underline"
                  href={ROUTES.legal.cgv}
                >
                  conditions générales
                </a>
                .
              </p>
            </div>
          </Reveal>
        </section>

        {/* ── Timeline ── */}
        <section>
          <Reveal>
            <h2 className="font-display text-2xl font-bold text-ink">Notre parcours</h2>
          </Reveal>
          <div className="relative mt-8">
            <div className="absolute bottom-0 left-[52px] top-0 w-px bg-stone-200" />
            <div className="space-y-6">
              {TIMELINE.map((item, i) => (
                <Reveal key={item.year} delay={i * 0.06}>
                  <div className="flex gap-5">
                    <div className="z-10 flex h-[28px] w-[44px] shrink-0 items-center justify-center rounded-full bg-brand-100 text-[11px] font-bold text-brand-700">
                      {item.year}
                    </div>
                    <p className="pt-0.5 text-[14px] leading-relaxed text-stone-700">
                      {item.event}
                    </p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* ── Équipe ── */}
        <section>
          <Reveal>
            <h2 className="font-display text-2xl font-bold text-ink">L'équipe</h2>
            <p className="mt-2 text-[14px] text-stone-500">Des experts dédiés à chaque métier.</p>
          </Reveal>
          <div className="mt-6 grid gap-4 sm:grid-cols-3">
            {TEAM.map((m, i) => (
              <Reveal key={m.name} delay={i * 0.06}>
                <div className="rounded-xl border border-stone-200 bg-stone-50/40 p-5 text-center">
                  <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-brand-100 font-display text-[15px] font-bold text-brand-700">
                    {m.initials}
                  </div>
                  <p className="mt-3 text-[14px] font-semibold text-ink">{m.name}</p>
                  <p className="text-[12px] text-stone-500">{m.role}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </section>

        {/* ── CTA ── */}
        <Reveal>
          <div className="rounded-2xl border border-brand-200 bg-brand-50/60 p-8 text-center">
            <h2 className="font-display text-xl font-bold text-ink">Prêt à démarrer ?</h2>
            <p className="mt-2 text-[14px] text-stone-600">
              Diagnostic gratuit en 5 minutes, sans engagement.
            </p>
            <div className="mt-5 flex flex-wrap justify-center gap-3">
              <a
                href="/outils"
                className="inline-flex items-center gap-2 rounded-xl bg-ink px-5 py-2.5 text-[14px] font-semibold text-white hover:bg-stone-800"
              >
                Diagnostiquer <ArrowRight className="h-4 w-4" />
              </a>
              <a
                href="/contact"
                className="inline-flex items-center gap-2 rounded-xl border border-stone-200 px-5 py-2.5 text-[14px] font-semibold text-ink hover:bg-stone-50"
              >
                <Users className="h-4 w-4" /> Nous contacter
              </a>
            </div>
          </div>
        </Reveal>
      </Container>

      <BreadcrumbListLd
        items={[
          { name: 'Accueil', url: 'https://solutionecofrance.fr/' },
          { name: 'À propos', url: 'https://solutionecofrance.fr/a-propos' },
        ]}
      />
    </div>
  );
}
